import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { GridLoader } from "react-spinners";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  buscarNotasEcredacApuracao,
  buscarClassificacaoSugeridaEcredac,
} from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import BreadcrumbApuracao from "../components/BreadcrumbApuracao";
import SelectFiltro, { BuscaFiltro } from "../components/SelectFiltro";
import { formatarCnpj } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import {
  CLASSIFICACOES,
  NAO_CLASSIFICADO,
  classificacaoDaNota,
  origemDaNota,
  definirClassificacao,
  definirSugestoesPorNcm,
} from "./ecredacClassificacaoStore";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";

const NUM_TH = { textAlign: "right" };
const NUM_TD = { textAlign: "right", fontFamily: "ui-monospace, monospace" };

const BADGE_POR_CLASSIFICACAO = {
  Insumo: { bg: "#e8f5e9", cor: "#2e7d32" },
  Uso: { bg: "#fff3e0", cor: "#e65100" },
  Consumo: { bg: "#e3f2fd", cor: "#1565c0" },
  [NAO_CLASSIFICADO]: { bg: "#eceff1", cor: "#546e7a" },
};

function exportarCsv(linhas, nomeArquivo) {
  const cols = [
    "cnpj", "competencia", "fluxo", "documento", "ncm",
    "icms_suportado", "valor_confronto", "icms_gera", "classificacao",
  ];
  const corpo = linhas.map((l) =>
    [
      l.cnpj, l.competencia, l.fluxo, l.documento, l.ncm,
      l.icmsSuportado, l.valorConfronto, l.icmsGera,
      classificacaoDaNota(l.documento, l.ncm),
    ]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(";")
  );
  const blob = new Blob([[cols.join(";"), ...corpo].join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * TXM-479 — "Lançamentos Ecredac — por nota": mesmo dado de AbaEcredac (grão nota/NCM, via
 * relatorios.consultar_ecredacsimplificada_notas), acrescido da coluna Classificação
 * (Insumo/Uso/Consumo). Classificação padrão vem do NCM cadastrado na Tabela NCM (TXM-594,
 * Fase 3 — buscarClassificacaoSugeridaEcredac, buscada uma vez por carregamento da tela);
 * NCM sem cadastro nasce "Não classificado", igual sempre foi. "Editar" grava um override
 * manual por documento (localStorage, ecredacClassificacaoStore.js — mesmo mecanismo do
 * protótipo ecredac-classificacao-store.js), que tem prioridade sobre o padrão do NCM e não
 * afeta as demais notas com o mesmo NCM.
 */
export default function LancamentosEcredac() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const voltar = searchParams.get("voltar");

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [versaoClassificacao, setVersaoClassificacao] = useState(0);
  const [busca, setBusca] = useState("");
  const [competenciaFiltro, setCompetenciaFiltro] = useState("");
  const [fluxoFiltro, setFluxoFiltro] = useState("");
  const [classificacaoFiltro, setClassificacaoFiltro] = useState("");
  const [linhasPagina, setLinhasPagina] = useState([]);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const [edicao, setEdicao] = useState(null);
  const [novaClassificacao, setNovaClassificacao] = useState("");

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setLoading(true);
      setErro("");
      // Mapa é estado de módulo: sem zerar, a apuração nova herdaria as sugestões da anterior.
      definirSugestoesPorNcm({});
      const res = await buscarNotasEcredacApuracao(id);
      if (!ativo) return;
      if (!res.sucesso) {
        setErro(res.mensagem || "Não foi possível carregar os lançamentos.");
        setLoading(false);
        return;
      }
      setLinhas(res.linhas);
      setLoading(false);

      // TXM-594 Fase 3 — sugestão de classificação por NCM. Buscada DEPOIS de liberar a
      // tela (não bloqueia o loading): a lista de notas é o essencial, a classificação é um
      // enriquecimento que chega logo em seguida — e buscarClassificacaoSugeridaEcredac já
      // falha "suave" (devolve {} em erro), então isso nunca vira um segundo estado de erro.
      const sugestoes = await buscarClassificacaoSugeridaEcredac(id);
      if (!ativo) return;
      definirSugestoesPorNcm(sugestoes);
      setVersaoClassificacao((v) => v + 1);
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [id]);

  const competenciaOpcoes = useMemo(() => {
    const distintas = Array.from(new Set(linhas.map((l) => l.competencia))).sort();
    return [{ value: "", label: "Todas" }, ...distintas.map((c) => ({ value: c, label: c }))];
  }, [linhas]);

  const fluxoOpcoes = [
    { value: "", label: "Todos" },
    { value: "VENDA", label: "Venda" },
    { value: "DEVOLUCAO", label: "Devolução" },
  ];

  const classificacaoOpcoes = [
    { value: "", label: "Todas" },
    ...CLASSIFICACOES.map((c) => ({ value: c, label: c })),
  ];

  // `versaoClassificacao` não é lido no corpo — só força o recálculo após um Salvar no
  // modal, já que classificacaoDaNota/origemDaNota leem localStorage por fora do estado
  // do React (mesmo mecanismo do protótipo).
  const linhasComClassificacao = useMemo(
    () =>
      linhas.map((l) => ({
        ...l,
        classificacao: classificacaoDaNota(l.documento, l.ncm),
        origemClassificacao: origemDaNota(l.documento, l.ncm),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linhas, versaoClassificacao]
  );

  const linhasFiltradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return linhasComClassificacao.filter((l) => {
      if (q && !l.ncm.toLowerCase().includes(q) && !l.documento.toLowerCase().includes(q)) return false;
      if (competenciaFiltro && l.competencia !== competenciaFiltro) return false;
      if (fluxoFiltro && l.fluxo !== fluxoFiltro) return false;
      if (classificacaoFiltro && l.classificacao !== classificacaoFiltro) return false;
      return true;
    });
  }, [linhasComClassificacao, busca, competenciaFiltro, fluxoFiltro, classificacaoFiltro]);

  function limparFiltros() {
    setBusca("");
    setCompetenciaFiltro("");
    setFluxoFiltro("");
    setClassificacaoFiltro("");
  }

  function abrirEdicao(linha) {
    setEdicao(linha);
    setNovaClassificacao(linha.classificacao);
  }

  function salvarEdicao() {
    definirClassificacao(edicao.documento, novaClassificacao);
    setEdicao(null);
    setVersaoClassificacao((v) => v + 1);
    setSnack({ open: true, msg: "Classificação da nota atualizada com sucesso.", severity: "success" });
  }

  function handleVoltar() {
    if (voltar) {
      navigate(voltar);
      return;
    }
    // `id` aqui é o id da apuração Ecredac (filha), não o do pai ICMS que /detalhe/icms
    // espera — mesmo padrão de LancamentosContribuições/LancamentosEcd/LancamentosEcf.
    navigate(-1);
  }

  const cnpjApuracao = linhas[0]?.cnpj;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  return (
    <BreadcrumbApuracao
      itens={[
        { label: "Apurações", onClick: () => navigate("/relatorios/apuracoes") },
        { label: `Apuração ICMS — ${cnpjApuracao ? formatarCnpj(cnpjApuracao) : "—"}`, onClick: handleVoltar },
        { label: "Lançamentos Ecredac" },
      ]}
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Cada linha é uma nota da view ecredac5. A Classificação é preenchida automaticamente a
        partir do NCM da nota cadastrado na Tabela NCM (Insumo, Uso ou Consumo); quando o NCM
        não está cadastrado, a nota aparece como Não classificado. Use "Editar" para ajustar
        manualmente a classificação de uma nota específica — o ajuste manual tem prioridade
        sobre a classificação padrão do NCM e não afeta as demais notas com o mesmo NCM.
      </Alert>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <ApuracaoCardShell
        hideContainer
        innerCardSx={{ border: "none", margin: 0 }}
        conteudoComScrollProprio
        Icone={EditIcon}
        titulo="Lançamentos Ecredac — por nota"
        subtitulo="Créditos por nota (view ecredac5) com classificação por NCM"
        acaoHeader={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
              {linhasFiltradas.length} nota{linhasFiltradas.length !== 1 ? "s" : ""} no filtro
            </Typography>
            <Button
              variant="contained"
              size="small"
              disabled={linhasFiltradas.length === 0}
              onClick={() =>
                exportarCsv(linhasFiltradas, `lancamentos_ecredac_${formatarCnpj(cnpjApuracao)}.csv`)
              }
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
            >
              Exportar CSV
            </Button>
          </Box>
        }
        filtros={
          <>
            <BuscaFiltro
              label="NCM ou documento"
              placeholder="Pesquisar código ou chave"
              value={busca}
              onChange={setBusca}
            />
            <SelectFiltro label="Competência" value={competenciaFiltro} onChange={setCompetenciaFiltro} options={competenciaOpcoes} />
            <SelectFiltro label="Fluxo" value={fluxoFiltro} onChange={setFluxoFiltro} options={fluxoOpcoes} />
            <SelectFiltro label="Classificação" value={classificacaoFiltro} onChange={setClassificacaoFiltro} options={classificacaoOpcoes} />
            <Button
              variant="outlined"
              size="small"
              onClick={limparFiltros}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
            >
              Limpar filtros
            </Button>
          </>
        }
        paginacao={
          linhasFiltradas.length > 0 && (
            <Pagination
              paginationKey="lancamentos-ecredac"
              data={linhasFiltradas}
              filteredData={linhasPagina}
              setFilteredData={setLinhasPagina}
            />
          )
        }
      >
        {linhasFiltradas.length === 0 ? (
          <p className={styles.emptyMsg}>Nenhuma nota encontrada com os filtros informados.</p>
        ) : (
          <div style={{ margin: "8px 0 0", overflow: "auto" }}>
            <table className={tableStyles.accordion_table}>
              <thead>
                <tr>
                  <th scope="col" className={`${tableStyles.stickyKeyHeader} ${tableStyles.stickyKeyCol1}`}>Competência</th>
                  <th scope="col">Fluxo</th>
                  <th scope="col">Documento / Chave</th>
                  <th scope="col">NCM</th>
                  <th scope="col" style={NUM_TH}>ICMS Suportado</th>
                  <th scope="col" style={NUM_TH}>Valor Confronto</th>
                  <th scope="col" style={NUM_TH}>ICMS Gera</th>
                  <th scope="col">Classificação</th>
                  <th scope="col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {linhasPagina.map((linha, idx) => {
                  const badge = BADGE_POR_CLASSIFICACAO[linha.classificacao] || BADGE_POR_CLASSIFICACAO[NAO_CLASSIFICADO];
                  return (
                    <tr key={`${linha.documento}-${linha.ncm}-${idx}`}>
                      <td className={`${tableStyles.stickyKeyCell} ${tableStyles.stickyKeyCol1}`}><strong>{linha.competencia}</strong></td>
                      <td>{linha.fluxo === "VENDA" ? "Venda" : "Devolução"}</td>
                      <td className={tableStyles.mono || ""} title={linha.documento}>
                        {linha.documento.length > 15 ? `${linha.documento.slice(0, 15)}…` : linha.documento}
                      </td>
                      <td>{linha.ncm || "—"}</td>
                      <td style={NUM_TD}>{formatarMoedaBRL(linha.icmsSuportado)}</td>
                      <td style={NUM_TD}>{formatarMoedaBRL(linha.valorConfronto)}</td>
                      <td style={{ ...NUM_TD, color: linha.icmsGera > 0 ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                        {formatarMoedaBRL(linha.icmsGera)}
                      </td>
                      <td>
                        <span style={{ padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: badge.bg, color: badge.cor }}>
                          {linha.classificacao}
                        </span>
                        {linha.origemClassificacao === "manual" && (
                          <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 700, textTransform: "uppercase", background: "#f5f5f5", color: "#777" }}>
                            manual
                          </span>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => abrirEdicao(linha)}
                          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, minHeight: 32, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
                        >
                          Editar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ApuracaoCardShell>

      <Dialog open={!!edicao} onClose={() => setEdicao(null)} fullWidth maxWidth="sm">
        <DialogTitle>Editar classificação da nota</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 0.5 }}>
            <strong>Documento / Chave:</strong> {edicao?.documento}
          </Typography>
          <Typography sx={{ mb: 2 }}>
            <strong>NCM:</strong> {edicao?.ncm || "—"}
          </Typography>
          <TextField
            select
            fullWidth
            label="Classificação"
            value={novaClassificacao}
            onChange={(e) => setNovaClassificacao(e.target.value)}
          >
            {CLASSIFICACOES.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
          <Alert severity="info" sx={{ mt: 2 }}>
            Vale apenas para esta nota. As demais notas com o mesmo NCM continuam usando a
            classificação cadastrada na Tabela NCM.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdicao(null)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarEdicao} sx={{ bgcolor: "#18316e", "&:hover": { bgcolor: "#152858" } }}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity}>{snack.msg}</Alert>
      </Snackbar>
    </BreadcrumbApuracao>
  );
}
